import { useEffect, useState } from 'react';
import { FiX, FiCheck, FiLoader } from 'react-icons/fi';
import { SearchableSelect } from '../SearchableSelect';
import styles from './QuickAddModal.module.css';

interface SelectOption {
    value: string;
    label: string;
}

interface QuickAddModalProps {
    title: string;
    fields: {
        nome: { label: string; placeholder: string };
        categoriaPai?: { label: string; options: SelectOption[]; defaultValue?: string };
    };
    onConfirm: (data: { nome: string; categoriaPaiId?: string }) => Promise<void>;
    onClose: () => void;
    variant?: 'success' | 'danger';
}

export const QuickAddModal = ({ title, fields, onConfirm, onClose, variant = 'success' }: QuickAddModalProps) => {
    const [nome, setNome] = useState('');
    const [categoriaPaiId, setCategoriaPaiId] = useState(fields.categoriaPai?.defaultValue ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setCategoriaPaiId(fields.categoriaPai?.defaultValue ?? '');
    }, [fields.categoriaPai?.defaultValue]);

    const handleSubmit = async () => {
        const trimmed = nome.trim();
        if (!trimmed) {
            setError('O nome não pode ser vazio.');
            return;
        }
        if (fields.categoriaPai && !categoriaPaiId) {
            setError('Selecione a categoria pai.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await onConfirm({ nome: trimmed, categoriaPaiId: fields.categoriaPai ? categoriaPaiId : undefined });
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Erro ao salvar. Tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSubmit();
        if (e.key === 'Escape') onClose();
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h3>{title}</h3>
                    <button className={styles.closeBtn} onClick={onClose} type="button">
                        <FiX size={18} />
                    </button>
                </div>
                <div className={styles.body}>
                    <label className={styles.fieldLabel}>{fields.nome.label}</label>
                    <input
                        type="text"
                        className={styles.input}
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={fields.nome.placeholder}
                        autoFocus
                        maxLength={100}
                    />
                    {fields.categoriaPai && (
                        <>
                            <label className={styles.fieldLabel}>{fields.categoriaPai.label}</label>
                            <SearchableSelect
                                options={fields.categoriaPai.options}
                                value={categoriaPaiId}
                                onChange={setCategoriaPaiId}
                                placeholder="Selecione..."
                                fullWidth
                            />
                        </>
                    )}
                    {error && <p className={styles.error}>{error}</p>}
                </div>
                <div className={styles.footer}>
                    <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <button type="button" className={`${styles.confirmBtn}${variant === 'danger' ? ` ${styles.confirmBtnDanger}` : ''}`} onClick={handleSubmit} disabled={saving}>
                        {saving ? <FiLoader size={16} /> : <FiCheck size={16} />}
                        {saving ? 'Salvando...' : 'Adicionar'}
                    </button>
                </div>
            </div>
        </div>
    );
};